import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BookIcon from '@mui/icons-material/Book';
import QuizIcon from '@mui/icons-material/Quiz';
import SchoolIcon from '@mui/icons-material/School';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ModuleCard from './ModuleCard';
import Layout from './Layout';
import { modulesList } from '../data/modules';
import { useProgress } from '../hooks/useProgress';

// Styled components for modern design
const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderRadius: '32px',
  padding: theme.spacing(8, 4),
  marginBottom: theme.spacing(8),
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    opacity: 0.4,
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(6, 3),
    marginBottom: theme.spacing(6),
  },
}));

const StatCard = styled(Paper)(({ theme }) => ({
  background: '#1a1a2e',
  borderRadius: '20px',
  padding: theme.spacing(4),
  textAlign: 'center',
  border: '1px solid rgba(102, 126, 234, 0.1),',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
    '& .stat-icon': {
      transform: 'rotate(10deg) scale(1.1)',
    },
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-50%',
    width: '200%',
    height: '200%',
    background: 'radial-gradient(circle, rgba(102, 126, 234, 0.05) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  marginBottom: theme.spacing(4),
  color: '#f1f5f9',
  position: 'relative',
  textAlign: 'center',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-12px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '80px',
    height: '5px',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '10px',
  },
}));

const FeatureBox = styled(Box)(({ theme }) => ({
  background: '#1a1a2e',
  borderRadius: '24px',
  padding: theme.spacing(4),
  textAlign: 'center',
  border: '1px solid rgba(102, 126, 234, 0.1),',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2.5),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    '& .feature-icon': {
      transform: 'scale(1.1) rotate(5deg)',
    },
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '72px',
  height: '72px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease',
  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
}));

export default function Home() {
  const progress = useProgress();
  
  // Calculate statistics
  const totalModules = modulesList.length;
  const completedModules = Object.values(progress).filter(p => p.done).length;
  const averageScore = Object.values(progress).filter(p => p.done && p.score)
    .reduce((acc, p, _, arr) => acc + (p.score / arr.length), 0);

  return (
    <Layout>
      {/* Hero Section */}
      <HeroSection>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ mb: 4 }}>
            <SchoolIcon sx={{ fontSize: '5rem', color: '#ffffff', mb: 3, filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }} />
          </Box>
          <Typography 
            variant="h1" 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontWeight: 900, 
              color: '#ffffff',
              mb: 3,
              textShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            Sua Aprovação Começa Aqui!
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.95)', 
              maxWidth: '700px', 
              mx: 'auto', 
              lineHeight: 1.8,
              mb: 4,
              fontWeight: 400,
            }}
          >
            Domine os conteúdos mais importantes para concursos públicos com nossa plataforma 
            de estudos inteligente e interativa.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                background: '#e2e8f0',
                color: '#667eea',
                borderRadius: '12px',
                px: 4,
                py: 1.5,
                fontWeight: 600,
                fontSize: '1.1rem',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                '&:hover': {
                  background: '#f1f5f9',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
                },
              }}
              onClick={() => document.getElementById('modules-section').scrollIntoView({ behavior: 'smooth' })}
            >
              Começar Agora
            </Button>
            <Button
              component={RouterLink}
              to="/conquistas"
              variant="outlined"
              size="large"
              startIcon={<EmojiEventsIcon />}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.8)',
                color: '#ffffff',
                borderRadius: '12px',
                px: 4,
                py: 1.5,
                fontWeight: 600,
                fontSize: '1.1rem',
                borderWidth: 2,
                '&:hover': {
                  borderColor: '#ffffff',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderWidth: 2,
                },
              }}
            >
              Ver Conquistas
            </Button>
          </Box>
        </Box>
      </HeroSection>

      {/* Statistics Section */}
      <Box sx={{ mb: 10 }}>
        <SectionTitle variant="h2" component="h2" align="center">
          Acompanhe seu Desempenho
        </SectionTitle>
        <Typography 
          variant="body1" 
          align="center" 
          sx={{ 
            color: '#cbd5e1', 
            mb: 6, 
            maxWidth: '600px', 
            mx: 'auto',
            fontSize: '1.125rem',
          }}
        >
          Monitore seu progresso em tempo real e identifique áreas para melhorar
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <StatCard elevation={0}>
              <IconWrapper sx={{ mx: 'auto' }} className="stat-icon">
                <BookIcon sx={{ color: 'white', fontSize: '2rem' }} />
              </IconWrapper>
              <Typography variant="h2" sx={{ fontWeight: 800, color: '#f1f5f9', mb: 1 }}>
                {totalModules}
              </Typography>
              <Typography variant="h6" sx={{ color: '#cbd5e1', fontWeight: 500 }}>
                Módulos Disponíveis
              </Typography>
              <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
                Conteúdo completo e atualizado
              </Typography>
            </StatCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard elevation={0}>
              <IconWrapper sx={{ mx: 'auto' }} className="stat-icon">
                <TrendingUpIcon sx={{ color: 'white', fontSize: '2rem' }} />
              </IconWrapper>
              <Typography variant="h2" sx={{ fontWeight: 800, color: '#f1f5f9', mb: 1 }}>
                {completedModules}
              </Typography>
              <Typography variant="h6" sx={{ color: '#cbd5e1', fontWeight: 500 }}>
                Módulos Concluídos
              </Typography>
              <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
                Continue assim!
              </Typography>
            </StatCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard elevation={0}>
              <IconWrapper sx={{ mx: 'auto' }} className="stat-icon">
                <QuizIcon sx={{ color: 'white', fontSize: '2rem' }} />
              </IconWrapper>
              <Typography variant="h2" sx={{ fontWeight: 800, color: '#f1f5f9', mb: 1 }}>
                {averageScore ? Math.round(averageScore) : 0}%
              </Typography>
              <Typography variant="h6" sx={{ color: '#cbd5e1', fontWeight: 500 }}>
                Pontuação Média
              </Typography>
              <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
                Nos quizzes realizados
              </Typography>
            </StatCard>
          </Grid>
        </Grid>
      </Box>

      {/* Modules Section */}
      <Box sx={{ mb: 10 }} id="modules-section">
        <SectionTitle variant="h2" component="h2" align="center">
          Módulos de Estudo
        </SectionTitle>
        <Typography 
          variant="body1" 
          align="center" 
          sx={{ 
            color: '#cbd5e1', 
            mb: 6, 
            maxWidth: '700px', 
            mx: 'auto',
            fontSize: '1.125rem',
            lineHeight: 1.8,
          }}
        >
          Conteúdo estruturado e organizado para maximizar seu aprendizado. 
          Cada módulo foi cuidadosamente elaborado com resumos completos e quizzes interativos.
        </Typography>
        
        <Grid container spacing={4}>
          {modulesList.map((module) => (
            <Grid key={module.id} item xs={12} md={6}>
              <ModuleCard module={module} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Features Section */}
      <Box sx={{ mb: 10, py: 8, background: '#1a1a2e', borderRadius: '32px' }}>
        <SectionTitle variant="h2" component="h2" align="center">
          Por que escolher nossa plataforma?
        </SectionTitle>
        <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
          <Grid item xs={12} md={4}>
            <FeatureBox>
              <IconWrapper className="feature-icon">
                <BookIcon sx={{ color: 'white', fontSize: '2rem' }} />
              </IconWrapper>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#f1f5f9' }}>
                Conteúdo Estruturado
              </Typography>
              <Typography variant="body1" sx={{ color: '#cbd5e1', textAlign: 'center' }}>
                Resumos organizados e atualizados com as principais matérias de concursos públicos, 
                elaborados por especialistas.
              </Typography>
            </FeatureBox>
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureBox>
              <IconWrapper className="feature-icon">
                <QuizIcon sx={{ color: 'white', fontSize: '2rem' }} />
              </IconWrapper>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#f1f5f9' }}>
                Quizzes Interativos
              </Typography>
              <Typography variant="body1" sx={{ color: '#cbd5e1', textAlign: 'center' }}>
                Teste seus conhecimentos com questões cuidadosamente elaboradas que simulam 
                o formato real das provas.
              </Typography>
            </FeatureBox>
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureBox>
              <IconWrapper className="feature-icon">
                <TrendingUpIcon sx={{ color: 'white', fontSize: '2rem' }} />
              </IconWrapper>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#f1f5f9' }}>
                Acompanhamento Inteligente
              </Typography>
              <Typography variant="body1" sx={{ color: '#cbd5e1', textAlign: 'center' }}>
                Monitore seu progresso em tempo real e receba insights sobre áreas 
                que precisam de mais atenção.
              </Typography>
            </FeatureBox>
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
}
